<template>
	<div>
		<InfiniteBiScroll
			v-slot="{ anchorName, pageStart, pageNumber }"
			:pagination-level="paginationLevel"
			:loading="loading"
			:pageSize="titlesPerPage"
			:titlesAmount="titlesAmount"
			@load-next="$emit('load-next', $event)"
			@load-prev="$emit('load-prev', $event)"
		>
			<div class="title-list-grid">
				<!-- we need this div wrapper otherwise we have funky node mismathes (even in CSR!) -->

				<div
					class="title-list-grid__sr-campaign"
					:class="{
						'title-list-grid__sr-campaign--hasCampaign': hasActiveSRCampaign,
					}"
				>
					<ClientOnly>
						<slot name="sponsoredRecommendation" />
					</ClientOnly>
				</div>

				<div
					v-for="(title, index) in titles"
					class="title-list-grid__item"
					:data-id="pageNumber(pageStart, index, titlesPerPage) + '_' + requestHash + '_' + title.node.id"
					:key="pageNumber(pageStart, index, titlesPerPage) + '_' + requestHash + '_' + title.node.id"
					data-testid="titleItem"
					:data-title="title.node.content.title"
				>
					<!-- anchor div: keep track of `page` parameter when scrolling for <InfiniteBiScroll> -->
					<div
						v-if="index % titlesPerPage === 0"
						:data-page-number="pageNumber(pageStart, index, titlesPerPage)"
						:data-page="anchorName"
					></div>

					<TitleDetailLink
						class="title-list-grid__item--link"
						:fullPath="title.node.content.fullPath"
						:title="getTitle(title)"
						@clickOut="$emit('clickOut')"
					>
						<PosterGraphql
							:title="title.node"
							:forceQuickActions="isFirstPage"
							:showQuickActions="isLoggedIn && shouldRender"
							:showRibbon="!isLoggedIn"
							:showFreeTag="showFreeTag"
							:index="index"
							:watchNowOffer="getWatchNowOffer(title)"
							:lazy="isLazyPoster(index)"
							:freeOffersCount="getFreeOffersCount(title)"
							@click="onPosterClick(title, index)"
							ref="posters"
						/>
					</TitleDetailLink>
				</div>
			</div>
		</InfiniteBiScroll>

		<InfiniteScroll
			v-if="paginationLevel === 0"
			:disabled="disableInfiniteScroll"
			:loading="loading"
			@load-next="$emit('load-next', $event)"
			ref="infiniteScroll"
		/>
	</div>
</template>

<script setup lang="ts">
import type { CollectionType } from '@/enums/collection-type';
import type { TitleEventPayloadGraphql } from '@/helpers/title-helper';
import type { PopularTitleGraphqlFragment } from '@/pages/graphql/fragments/PopularTitle.fragment';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type { UpcomingTitleGraphqlFragment } from '@/pages/graphql/fragments/UpcomingTitle.fragment';

import InfiniteBiScroll from '@/components/list/InfiniteBiScroll.vue';
import InfiniteScroll from '@/components/list/InfiniteScroll.vue';
import TitleDetailLink from '@/components/nav/links/TitleDetailLink.vue';
import PosterGraphql from '@/components/poster/PosterGraphql.vue';

import { ImpressionTrackingEvents } from '@/enums/events';
import { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
import { btoa } from '@/helpers/base64-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { useFilterStore } from '@/stores/pinia/useFilterStore';
import { useMounted } from '@vueuse/core';
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { PaginationLevel } from '@/stores/pinia/useMetaStore';

type Title = { node: PopularTitleGraphqlFragment | UpcomingTitleGraphqlFragment };

const props = withDefaults(
	defineProps<{
		hasNoTitles?: boolean;
		isAtEndOfList?: boolean;
		isFirstPage: boolean;
		loading: boolean;
		titles: Title[];
		titlesAmount: number;
		titlesPerPage?: number;
		collectionType?: CollectionType;
		sponsoredAd?: SponsoredAdFragment | undefined;
		paginationLevel?: PaginationLevel;
	}>(),
	{
		titlesAmount: 0,
		titlesPerPage: 100,
		sponsoredAd: undefined,
		paginationLevel: 0,
	}
);

const emits = defineEmits<{
	(e: 'onPosterClick', data: { title: TitleEventPayloadGraphql['title']; index: number; label: 'free' | '' }): void;
	(e: 'clickOut'): void;
	(e: 'load-next', data: any): void;
	(e: 'load-prev', data: any): void;
}>();

const { filters } = useFilterStore();
const { isLoggedIn } = storeToRefs(useUserStore());
const { activeRoute } = storeToRefs(useRoutingStore());

const isMounted = useMounted();

const freeTagRoutes = [
	'app.titles.popular.list',
	'app.titles.popular.movie',
	'app.titles.popular.show',
	'app.titles.popular.subgenre',
	'app.titles.popular.subgenre-movie',
	'app.titles.popular.subgenre-show',
];
const showFreeTag = computed(() => freeTagRoutes.includes(activeRoute.value?.name as string) && !isLoggedIn.value);

const disableInfiniteScroll = computed(() => {
	const isTitleDetailPage = activeRoute.value?.name?.includes('app.detail');
	return props.hasNoTitles || props.isAtEndOfList || props.loading || isTitleDetailPage;
});

// CLS issue: upon filtering, the posters may change their position e.g. from 1st to 3rd.
// this change of order, together with a not-perfect mobile connection that will take more than 500ms
// for the filtering request, will be picked up as CLS: the existing element changes its position.
// to circumvent this, we give the posters a :key that is sort of bound to the request, so we can re-create
// the posters instead of being reused and moved through the DOM.
const requestHash = computed(() => {
	try {
		return btoa(JSON.stringify(filters(props.collectionType)));
	} catch {
		return props.collectionType?.toString();
	}
});

const runtimeConfig = useRuntimeConfig().public;
const shouldRender = computed(() => !runtimeConfig.ssrMode || isMounted.value);

/** Sponsored Recommendation */
const sponsoredRecommendationPackageId = computed(() => props.sponsoredAd?.campaign?.watchNowOffer.package.packageId);

const hasActiveSRCampaign = computed(
	() => props.sponsoredAd?.campaign != null && props.sponsoredAd?.holdoutGroup == false
);

// Check to see if we should pass in title context
function getTitleContext(title: Title, index: number) {
	return new SnowplowTitleContextGraphql(title.node.objectId, title.node.objectType, null, null, null, index);
}

function onPosterClick(title: Title, index: number): TitleEventPayloadGraphql {
	if (!!sponsoredRecommendationPackageId.value && props.sponsoredAd?.campaign?.node.nodeId === title.node.id) {
		TrackingHelper.trackEvent(
			ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
			{
				action: 'click',
				label: `${title.node.id}_${sponsoredRecommendationPackageId.value}`,
				property: 'poster',
			},
			[getTitleContext(title, index)]
		);
	}

	const label = showFreeTag.value && getFreeOffersCount(title) ? 'free' : '';

	emits('onPosterClick', { title: title.node as TitleEventPayloadGraphql['title'], index, label });

	return { title: title.node as TitleEventPayloadGraphql['title'], index, label };
}

function isLazyPoster(index: number) {
	return runtimeConfig.ssrMode ? index >= runtimeConfig.imgPosterLoadCount : true;
}

function getFreeOffersCount({ node: title }: Title) {
	return (title as PopularTitleGraphqlFragment)?.freeOffersCount ?? 0;
}

function getTitle({ node: title }: Title) {
	return title.__typename === 'Season' ? title.show : title;
}

function getWatchNowOffer({ node: title }: Title) {
	return title.__typename === 'Season' ? title.show.watchNowOffer : title.watchNowOffer;
}

/**
 * Resets the internal state of the infinite scroll. Needs to be called at each filter change.
 * This method is used in parent component via reference
 */
// @ts-ignore the typing is correct for Vue 3 but Vue 2 sees typeof InfiniteScroll as '{}'
const infiniteScroll = ref<InstanceType<typeof InfiniteScroll> | null>(null);
function resetInfiniteScroll() {
	infiniteScroll.value?.reset();
}

defineExpose({ resetInfiniteScroll });
</script>

<style lang="scss">
@import '~@/styles/variables.scss';
@import '~@/styles/mixins.scss';

.title-list-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(($jw-title-poster-width - 20), 1fr));
	grid-gap: var(--jw-spacing-4);

	@include padding-two-row-viewport();

	.title-list-grid__sr-campaign {
		position: absolute;
		z-index: -1;

		&--hasCampaign {
			position: relative;
			z-index: 1;

			grid-row-start: 1;
			grid-column: span 2;
		}
	}

	// On mobile, 2 portrait columns produce approx. 1.41:1 container
	// so gap 25px + aspect-ratio 3/2 forces the SR cell to match the asset size and avoid cropping (case only popular page)
	@media (max-width: $screen-xs-max) {
		&:has(.title-list-grid__sr-campaign--hasCampaign) {
			column-gap: 25px;
		}

		.title-list-grid__sr-campaign--hasCampaign {
			aspect-ratio: 3/2;
			align-self: start;

			> * {
				height: 100%;
			}

			.sponsored-recommendation {
				height: 100%;
			}
		}
	}

	// when the grid goes down to 1 column SR shouldn't span 2 or it hides other posters
	@media (max-width: 311px) {
		.title-list-grid__sr-campaign--hasCampaign {
			grid-column: 1;
		}
	}

	@media (max-width: $screen-xs-max) {
		grid-template-columns: repeat(auto-fill, minmax(($jw-title-poster-width-sm - 20), 1fr));
	}

	@media (max-width: $screen-xxs) {
		grid-template-columns: repeat(auto-fill, minmax(($jw-title-poster-width-xs - 20), 1fr));
		grid-gap: var(--jw-spacing-2);
	}
}

.pointer-none {
	pointer-events: none;
}
</style>
